import { type FormEvent, useState } from 'react';

import { useT } from '@/i18n/LocaleProvider';

import { useAuthContext } from './AuthContext';

export type AuthPageProps = {
  onLoginLink?: () => void;
  onRegisterLink?: () => void;
  onSuccess?: () => void;
};

export type ChangePasswordPageProps = {
  onCancel?: () => void;
  onLoginLink?: () => void;
};

export function LoginPage({ onRegisterLink, onSuccess }: AuthPageProps) {
  const t = useT();
  const auth = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await auth.login(email, password);
      onSuccess?.();
    } catch (submitError) {
      setError(errorMessage(submitError, t('auth_error_login')));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="auth-screen" aria-labelledby="login-title">
      <div className="auth-copy">
        <p>{t('auth_contributor_access')}</p>
        <h1 id="login-title">{t('auth_login')}</h1>
      </div>

      <form className="auth-form" onSubmit={handleSubmit}>
        <label>
          <span>{t('auth_email')}</span>
          <input
            autoComplete="email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>

        <label>
          <span>{t('auth_password_label')}</span>
          <input
            autoComplete="current-password"
            name="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>

        {error ? <div className="form-error">{error}</div> : null}

        <div className="form-actions">
          <button className="primary-action" type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('auth_logging_in') : t('auth_login')}
          </button>
          <button className="text-action" type="button" onClick={onRegisterLink}>
            {t('auth_register')}
          </button>
        </div>
      </form>
    </section>
  );
}

export function RegisterPage({ onLoginLink, onSuccess }: AuthPageProps) {
  const t = useT();
  const auth = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('auth_passwords_mismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      await auth.register(email, password, confirmPassword);
      onSuccess?.();
    } catch (submitError) {
      setError(errorMessage(submitError, t('auth_error_register')));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="auth-screen" aria-labelledby="register-title">
      <div className="auth-copy">
        <p>{t('auth_contributor_access')}</p>
        <h1 id="register-title">{t('auth_register')}</h1>
      </div>

      <form className="auth-form" onSubmit={handleSubmit}>
        <label>
          <span>{t('auth_email')}</span>
          <input
            autoComplete="email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>

        <label>
          <span>{t('auth_password_label')}</span>
          <input
            autoComplete="new-password"
            minLength={8}
            name="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>

        <label>
          <span>{t('auth_confirm_password')}</span>
          <input
            autoComplete="new-password"
            minLength={8}
            name="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />
        </label>

        {error ? <div className="form-error">{error}</div> : null}

        <div className="form-actions">
          <button className="primary-action" type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('auth_creating_account') : t('auth_create_account')}
          </button>
          <button className="text-action" type="button" onClick={onLoginLink}>
            {t('auth_already_have_account')}
          </button>
        </div>
      </form>
    </section>
  );
}

export function ChangePasswordPage({ onCancel, onLoginLink }: ChangePasswordPageProps) {
  const t = useT();
  const auth = useAuthContext();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (newPassword !== confirmPassword) {
      setError(t('auth_new_passwords_mismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      await auth.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNotice(t('auth_password_changed'));
    } catch (submitError) {
      setError(errorMessage(submitError, t('auth_error_change_password')));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="auth-screen" aria-labelledby="password-title">
      <div className="auth-copy">
        <p>{t('auth_account_security')}</p>
        <h1 id="password-title">{t('auth_change_password')}</h1>
      </div>

      {!auth.isAuthenticated ? (
        <div className="auth-required">
          <strong>{t('auth_contributor_required')}</strong>
          <span>{t('auth_password_account_hint')}</span>
          <div className="form-actions">
            <button className="primary-action" type="button" onClick={onLoginLink}>
              {t('auth_login')}
            </button>
          </div>
        </div>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit}>
          <label>
            <span>{t('auth_current_password')}</span>
            <input
              autoComplete="current-password"
              name="current-password"
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
            />
          </label>

          <label>
            <span>{t('auth_new_password')}</span>
            <input
              autoComplete="new-password"
              minLength={8}
              name="new-password"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
          </label>

          <label>
            <span>{t('auth_confirm_password')}</span>
            <input
              autoComplete="new-password"
              minLength={8}
              name="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
          </label>

          {error ? <div className="form-error">{error}</div> : null}
          {notice ? <div className="form-notice">{notice}</div> : null}

          <div className="form-actions">
            <button className="primary-action" type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('auth_saving') : t('auth_save')}
            </button>
            <button className="text-action" type="button" onClick={onCancel}>
              {t('auth_cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}
